import React from "react";
import { useNavigate } from "react-router-dom";
import { useStoreState } from "ezStore";
import moment from "moment";
import { intl } from "LocaleWrapper";
import { useApolloClient, useMutation } from "@apollo/client";

import { Table, Filter, FilterValues, PageHeader, Card, Button, FilterTypes, QuickFilterTypes } from "@cofano/cck";

import { Box } from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { TableId, useBackendTable } from "components/table";
import { useConfirm } from "components/hooks/useConfirm";
import Helmet from "components/helmet/Helmet";

import { DataFormat, getPluginSettingValue, hasPluginPermission, usePermission } from "imports/Utils";

import ShipSelect, { ShipSelectOption } from "view/ships/components/selects/ShipSelect";
import StatusChip from "view/statuses/components/changer/StatusChip";
import SailingReportStatusSelect, {
	SailingReportStatusSelectOption,
} from "view/sailingreports/components/selects/SailingReportStatusSelect";
import SailingReportStatusChanger from "view/sailingreports/components/status/SailingReportStatusChanger";

import { ShipLink, VoyageLink } from "utils/LinkUtils";
import { FormattedDate, FormattedDateWindow } from "utils/DateUtils";

import SailingReportsDownloadPopover from "./components/popover/SailingReportsDownloadPopover";
import SailingReportDownloadButton from "./components/button/SailingReportDownloadButton";
import useDownloadSailingReport from "./utils/useDownloadSailingReport";
import { SailingReportRangeType } from "./utils/SailingReportUtils";

import { ChildPropsFromPagination } from "types/commons";

import { SearchSailingReportsDocument, SearchSailingReportsQuery } from "__generated__/view/sailingreports/graphql/sailingReport.queries.generated";
import { DeleteSailingReportDocument } from "__generated__/view/sailingreports/graphql/sailingReport.mutations.generated";
import { SortDirection } from "__generated__/enums";

export type SailingReportFromQuery = ChildPropsFromPagination<SearchSailingReportsQuery, "searchSailingReports">

export type SailingReportsFilter = {
	ship?: ShipSelectOption[];
	voyageNumber?: string;
	fromTime?: [number | null, number | null];
	remark?: string;
	status?: SailingReportStatusSelectOption | null;
}

const SailingReports = () => {

	const client = useApolloClient();
	const session = useStoreState(state => state.session);
	const navigate = useNavigate();

	const confirm = useConfirm();
	const { isSkipperOrShipManager, isReadOnly, isFinance, isAdmin } = usePermission();

	const { onDownload } = useDownloadSailingReport();

	const rangeType = getPluginSettingValue(session, "sailingReportRangeType");

	const getSearchObject = (filter: SailingReportsFilter) => {
		return {
			ship: filter.ship ? filter.ship.map((it) => it.id) : undefined,
			voyageNumber: filter.voyageNumber ?? undefined,
			fromTime: filter.fromTime?.[0] ?? undefined,
			toTime: filter.fromTime?.[1] ?? undefined,
			remark: filter.remark ?? undefined,
			status: filter.status?.name ?? undefined,
		};
	};

	const { tableProps, filterProps, filterValuesProps, refetch, refetchAfterDelete, filterObject, clearSelection } = useBackendTable({
		id: TableId.SailingReports,
		selectSettings: {
			selectionEnabled: true,
			isFullSelectEnabled: true,
		},
		onChange: async(pagination, sort, filter, callback) => {
			try {
				const response = await client.query({
					query: SearchSailingReportsDocument,
					variables: {
						search: getSearchObject(filter as SailingReportsFilter),
						pagination: {
							...pagination,
							sortList: sort ?? [ {
								field: "created",
								direction: SortDirection.Desc,
							} ],
						},
					},
				});

				callback({
					content: response.data.searchSailingReports?.content ?? [],
					totalResults: response.data.searchSailingReports?.totalElements ?? 0,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const [ removeSailingReport ] = useMutation(DeleteSailingReportDocument);

	const deleteSailingReport = (id: number) => {
		removeSailingReport({
			variables: {
				id: id,
			},
		}).then(() => {
			refetchAfterDelete();
		});
	};

	return (
		<React.Fragment>
			<PageHeader>
				<PageHeader.Title>
					{intl.formatMessage({ id: "SAILING_REPORTS" })}
					<Helmet title={intl.formatMessage({ id: "SAILING_REPORTS" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<SailingReportsDownloadPopover />

					{!isReadOnly && (
						<Button onClick={() => navigate("/view/sailingreports/create")}>
							{intl.formatMessage({ id: "ADD_BUTTON" })}
						</Button>
					)}
				</PageHeader.Actions>
			</PageHeader>

			{filterValuesProps.values.length > 0 && (
				<Box mb={2}>
					<Card disablePadding>
						<FilterValues
							{...filterValuesProps}
							fields={[ {
								id: "voyageNumber",
								title: intl.formatMessage({ id: "VOYAGE_NUMBER" }),
							}, {
								id: "ship",
								title: intl.formatMessage({ id: "SHIPS" }),
								renderFilterValue: (value) => value.map((option: ShipSelectOption) => option.name).join(", "),
							}, {
								id: "fromTime",
								title: intl.formatMessage({ id: "START_END_WINDOW" }),
								renderFilterValue: (value) => (
									<FormattedDateWindow
										from={value[0]}
										to={value[1]}
										format="ll"
										withoutYear />
								),
							}, {
								id: "remark",
								title: intl.formatMessage({ id: "SAILINGREPORT_REMARK" }),
							}, ...(hasPluginPermission(session, "sailingReportApprovals") ? [ {
								id: "status",
								title: intl.formatMessage({ id: "SAILINGREPORT_APPROVAL" }),
								renderFilterValue: (value: SailingReportStatusSelectOption) =>
									intl.formatMessage({ id: `${value.languageKeyPrefix}${value.name}` }),
							} ] : []) ]} />
					</Card>
				</Box>
			)}

			<Card disablePadding>
				<Table
					{...tableProps}
					onRowDoubleClick={(row, event) => {
						const controlPressed = event.ctrlKey;
						const url = `/view/sailingreports/${row.id}/details`;

						if (controlPressed) {
							navigate(url, { replace: false });
						} else {
							navigate(url);
						}
					}}
					filterComponent={() => (
						<Filter
							{...filterProps}
							fields={[ {
								id: "ship",
								title: intl.formatMessage({ id: "SHIP" }),
								disabled: isSkipperOrShipManager,
								render: (value, onChange) => (
									<ShipSelect
										value={value}
										onChange={options => onChange(options)}
										multiple />
								),
							}, {
								id: "voyageNumber",
								title: intl.formatMessage({ id: "VOYAGE_NUMBER" }),
								filterType: FilterTypes.TEXT,
							}, {
								id: "fromTime",
								title: intl.formatMessage({ id: "START_END_WINDOW" }),
								filterType: FilterTypes.DATE_RANGE,
							}, {
								id: "remark",
								title: intl.formatMessage({ id: "SAILINGREPORT_REMARK" }),
								filterType: FilterTypes.TEXT,
							}, ...(hasPluginPermission(session, "sailingReportApprovals") ? [ {
								id: "status",
								title: intl.formatMessage({ id: "SAILINGREPORT_APPROVAL" }),
								render: (value: SailingReportStatusSelectOption | null, onChange: (value: SailingReportStatusSelectOption | null) => void) => (
									<SailingReportStatusSelect
										currentStatus={null}
										value={value}
										onChange={(option) => onChange(option)}
										clearable />
								),
							} ] : []) ]} />
					)}
					columns={[ {
						id: "ship",
						title: intl.formatMessage({ id: "SHIP" }),
						render: (row: SailingReportFromQuery) => <ShipLink ship={row.ship} />,
						width: 200,
						sortable: true,
						disabled: isSkipperOrShipManager,
						filterComponent: (value, onChange) => (
							<ShipSelect
								label={intl.formatMessage({ id: "SHIP" })}
								value={value}
								onChange={options => onChange(options)}
								multiple />
						),
					}, {
						id: "voyageNumber",
						sortKey: "voyage.voyageNumber",
						title: intl.formatMessage({ id: "VOYAGE" }),
						render: (row: SailingReportFromQuery) => <VoyageLink voyage={row.voyage} />,
						sortable: true,
						width: 200,
						filterComponent: QuickFilterTypes.TEXT,
					}, {
						id: "fromTime",
						title: rangeType === SailingReportRangeType.Week ?
							intl.formatMessage({ id: "CONTRACT_WEEK" }) :
							intl.formatMessage({ id: "START_END_WINDOW" }),
						render: (row: SailingReportFromQuery) => {
							return rangeType === SailingReportRangeType.Week && row.fromTime ?
								`${moment(row.fromTime).isoWeek()} - ${moment(row.fromTime).year()}` :
								<FormattedDateWindow from={row.fromTime} to={row.toTime} format="ll" />;
						},
						sortable: true,
						width: 250,
						filterComponent: QuickFilterTypes.DATE_RANGE,
					}, {
						id: "sailMinutes",
						title: intl.formatMessage({ id: "SAILINGREPORT_SAILMINUTES" }),
						render: (row: SailingReportFromQuery) => DataFormat.minutesToHours(row.sailMinutes),
						sortable: true,
					}, {
						id: "bunkeringBefore",
						title: intl.formatMessage({ id: "BUNKERING_BEFORE" }),
						render: (row: SailingReportFromQuery) => DataFormat.volumeL(row.bunkeringBefore),
						sortable: true,
						width: 170,
					}, {
						id: "bunkered",
						title: intl.formatMessage({ id: "BUNKERED" }),
						render: (row: SailingReportFromQuery) => DataFormat.volumeL(row.bunkered),
						sortable: true,
					}, {
						id: "bunkeringAfter",
						title: intl.formatMessage({ id: "BUNKERING_AFTER" }),
						render: (row: SailingReportFromQuery) => DataFormat.volumeL(row.bunkeringAfter),
						sortable: true,
						width: 170,
					}, {
						id: "usageAmount",
						title: intl.formatMessage({ id: "USAGE_AMOUNT" }),
						render: (row: SailingReportFromQuery) => DataFormat.volumeL(row.usageAmount),
					}, {
						id: "emissionCO2",
						title: intl.formatMessage({ id: "EMISSION_CO2" }),
						sortable: true,
						render: (row: SailingReportFromQuery) => DataFormat.volumeKg(row.emissionCO2),
						width: 170,
					}, {
						id: "remark",
						title: intl.formatMessage({ id: "SAILINGREPORT_REMARK" }),
						sortable: true,
						width: 200,
						filterComponent: QuickFilterTypes.TEXT,
					}, {
						id: "created",
						title: intl.formatMessage({ id: "CREATED" }),
						render: (row: SailingReportFromQuery) => <FormattedDate date={row.created} withoutYear />,
						sortable: true,
						width: 150,
					}, ...(hasPluginPermission(session, "sailingReportApprovals") ? [ {
						id: "status",
						title: intl.formatMessage({ id: "SAILINGREPORT_APPROVAL" }),
						render: (row: SailingReportFromQuery) => {
							if (!row.status) {
								return "-";
							}

							return (isFinance || isAdmin) ? (
								<SailingReportStatusChanger
									sailingReportId={row.id}
									currentStatus={row.status}
									statusChanges={row.statusChanges}
									refetch={() => refetch()} />
							) : (
								<StatusChip
									label={intl.formatMessage({ id: `${row.status.languageKeyPrefix}${row.status.name}` })}
									color={row.status.color} />
							);
						},
						width: 170,
						filterComponent: (value: SailingReportStatusSelectOption | null, onChange: (value: SailingReportStatusSelectOption | null) => void) => (
							<SailingReportStatusSelect
								label={intl.formatMessage({ id: "SAILINGREPORT_APPROVAL" })}
								currentStatus={null}
								value={value}
								onChange={(option) => onChange(option)}
								clearable />
						),
					} ] : []) ]}
					actions={[ {
						render: (selection: SailingReportFromQuery[] | null, isFullSelect: boolean) => (
							<SailingReportDownloadButton
								selection={selection ?? []}
								isFullSelect={isFullSelect}
								filter={filterObject}
								onCompleted={() => clearSelection()} />
						),
					} ]}
					rowActions={[ {
						icon: <InsertDriveFileIcon />,
						onClick: (row) => onDownload([ row.id ], undefined, undefined),
					}, {
						icon: <EditIcon />,
						hidden: isReadOnly,
						onClick: (row) => navigate(`/view/sailingreports/${row.id}/edit`),
					}, {
						icon: <DeleteIcon />,
						hidden: isReadOnly,
						onClick: (row) => {
							confirm({
								type: "DELETE",
							}).then(() => {
								deleteSailingReport(row.id);
							});
						},
					} ]} />
			</Card>
		</React.Fragment>
	);
};

export default SailingReports;
